"use client";
import React, { useState } from "react";
import {
  Grid,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { HexColorPicker } from "react-colorful";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import config from "../../../../config";

const BrandingAppearance = ({
  isEditable,
  themeColor,
  setThemeColor,
  fontStyle,
  setFontStyle,
  logoUrl,
  setLogoUrl,
}) => {
  const [uploading, setUploading] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please upload an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const headers = config.getHeaders();
      if (headers['Content-Type']) {
        delete headers['Content-Type'];
      }

      const response = await fetch(`${config.API_BASE_URL}/client/store/upload/logo`, {
        method: "POST",
        headers: headers,
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.detail || result.message || "Upload failed");
      }

      setLogoUrl(result.logo_url);
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload logo: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Branding & Appearance
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Customize your chatbot's visual identity and appearance
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Logo Section - Top Full Width */}
        <Grid item xs={12}>
          <Box
            sx={{
              p: 3,
              border: "1px solid #E0E0E0",
              borderRadius: "12px",
              backgroundColor: "#FAFAFA",
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "15px", mb: 2 }}>
              Chatbot Logo
            </Typography>

            <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
              {/* Logo Preview */}
              <Box
                sx={{
                  width: 140,
                  height: 140,
                  borderRadius: "12px",
                  border: "2px solid #D5D9E2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#fff",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Logo preview"
                    style={{ width: "100%", height: "100%", objectFit: "contain", padding: "8px" }}
                  />
                ) : (
                  <Box sx={{ textAlign: "center" }}>
                    <CloudUploadIcon sx={{ fontSize: 40, color: "#999", mb: 1 }} />
                    <Typography variant="caption" color="textSecondary">
                      No logo
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Upload Section */}
              <Box sx={{ flex: 1 }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={uploading || !isEditable}
                  style={{ display: "none" }}
                  id="logo-upload"
                />
                <label htmlFor="logo-upload" style={{ display: "block" }}>
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    disabled={uploading || !isEditable}
                    sx={{ mb: 2 }}
                  >
                    {uploading ? "Uploading..." : "Upload Logo"}
                  </Button>
                </label>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                  Recommended: PNG or JPG, max 5MB
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block">
                  Recommended size: 200x200px or larger
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Theme Color - Left */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 3,
              border: "1px solid #E0E0E0",
              borderRadius: "12px",
              backgroundColor: "#FAFAFA",
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "15px", mb: 2 }}>
              Theme Color
            </Typography>

            <Box sx={{ mb: 2, opacity: !isEditable ? 0.6 : 1 }}>
              <HexColorPicker
                color={themeColor}
                onChange={setThemeColor}
                style={{ width: "100%", maxWidth: "220px", height: "160px" }}
              />
            </Box>

            {/* Preview + Hex Input */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 60,
                  height: 50,
                  borderRadius: "8px",
                  border: "2px solid #D5D9E2",
                  backgroundColor: themeColor,
                }}
              />
              <TextField
                size="small"
                value={themeColor}
                onChange={(e) => setThemeColor(e.target.value)}
                label="Hex Code"
                disabled={!isEditable}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        </Grid>

        {/* Font Style - Right */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              p: 3,
              border: "1px solid #E0E0E0",
              borderRadius: "12px",
              backgroundColor: "#FAFAFA",
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "15px", mb: 2 }}>
              Font Style
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Select Font</InputLabel>
              <Select
                value={fontStyle}
                onChange={(e) => setFontStyle(e.target.value)}
                label="Select Font"
                disabled={!isEditable}
              >
                <MenuItem value="Arial">Arial</MenuItem>
                <MenuItem value="Roboto">Roboto</MenuItem>
                <MenuItem value="Georgia">Georgia</MenuItem>
                <MenuItem value="Courier New">Courier New</MenuItem>
                <MenuItem value="Verdana">Verdana</MenuItem>
                <MenuItem value="Trebuchet MS">Trebuchet MS</MenuItem>
              </Select>
            </FormControl>

            {/* Font Preview */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                border: "1px solid #D5D9E2",
                borderRadius: "8px",
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontFamily: fontStyle,
                  fontSize: "16px",
                  color: "#333",
                }}
              >
                Ask Bud-i, Your best budtender.
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BrandingAppearance;
